import React, {createContext, useContext} from 'react';
import {get, isEqual} from 'lodash';
import {useAuth} from './AuthContext';
import {COMPANY_TYPE} from 'configs/types';
import {STRIPE_STATUS} from 'configs/membership';
import {COMPANY_STATUSES, IS_STAGE_DEV} from 'configs';
import {ENABLE_ISO_SURVEY, ENABLE_ANALYTICS} from 'configs/enable-features';
import {
  DEFAULT_PROCESSING_PAYMENT_RELOAD_DURATION,
  MIN_PROCESSING_PAYMENT_RELOAD_DURATION,
} from 'configs/import-management';
import useReload from 'hooks/useReload';
import useQuery from 'hooks/useQuery';

const MembershipContext = createContext({});

export function membershipGenerator(account, companyType) {
  const prevMeta = get(account, 'company.subscription.subscription_plans_meta');
  let subscriptionMeta = get(
    account,
    'company.subscription.subscriptions_meta'
  );

  const billingMethod = get(
    account,
    'company.subscription.subscriptions_billing_method'
  );

  if (!!prevMeta && typeof prevMeta === 'string') {
    subscriptionMeta = JSON.parse(prevMeta);
  }

  const permissions = get(subscriptionMeta, 'features');

  function hasPermit(pathPermit) {
    return get(permissions, pathPermit);
  }

  function getMetaValue(pathMeta) {
    return get(subscriptionMeta, pathMeta);
  }

  const isTrial = get(account, 'company.subscription.is_trial');
  const isTrialDone = get(account, 'company.subscription.trial_done');
  const subscriptionLevel = get(subscriptionMeta, 'level');

  const isSubscribed = subscriptionLevel > 0 && !isTrial;

  const isCE = companyType === COMPANY_TYPE.ce;

  const isCompany = companyType === COMPANY_TYPE.company;

  const isVU = isCE || isCompany;
  const isCompanySuspended = isEqual(
    get(account, 'company.company_status'),
    COMPANY_STATUSES.suspended
  );
  const stripeStatus = get(account, 'company.subscription.stripe_status');
  const isPastDue = isEqual(stripeStatus, STRIPE_STATUS.past_due);
  const isUnpaid = isEqual(stripeStatus, STRIPE_STATUS.unpaid);
  const isSuspendedSubscription = isVU && isUnpaid && isCompanySuspended;

  const subscriptionVersion = get(
    account,
    'company.subscription.subscription_plans_version'
  );

  const marketIntelligence = get(
    account,
    'company.subscription.market_intelligence'
  );

  // const addons = get(subscriptionMeta, 'addons');
  // const includedAddons = get(addons, 'included');
  // const optionalAddons = get(addons, 'optional');

  const isSubscribedToMI =
    marketIntelligence && ENABLE_ISO_SURVEY && ENABLE_ANALYTICS;

  const isProcessingPayment = get(
    account,
    'company.subscription.processing_payment'
  );
  const lastRenewalDate = get(account, 'company.subscription.last_renewed_at');

  // legacy user, is if version is 1
  const isLegacyUser =
    isEqual(subscriptionVersion, 1) &&
    (isTrial || isSubscribed || subscriptionLevel === 0 || isSubscribedToMI);

  const isRenewedSubscription = !isLegacyUser && !!lastRenewalDate;
  const isPendingPayment = isLegacyUser && isPastDue;
  const isFailedRenewal = !subscriptionLevel && isUnpaid;

  return {
    hasPermit,
    getMetaValue,
    isSubscribedToMI,
    permissions,
    isSubscribed,
    subscriptionLevel,
    subscriptionMeta,
    isSuspendedSubscription,
    isRenewedSubscription,
    billingMethod,
    isTrial,
    isTrialDone,
    stripeStatus,
    isLegacyUser,
    subscriptionVersion,
    isProcessingPayment,
    isPendingPayment,
    isFailedRenewal,
  };
}

function validateMilliseconds(milliseconds, minMilliseconds, maxMilliseconds) {
  if (typeof milliseconds !== 'number' || isNaN(milliseconds)) {
    return false;
  }

  if (milliseconds >= minMilliseconds && milliseconds <= maxMilliseconds) {
    return milliseconds;
  } else {
    return false;
  }
}

function MembershipProvider(props) {
  const {children} = props;

  const {account, companyType, handleInitialSession} = useAuth();
  const {query} = useQuery();
  const {processing_payment_reload_seconds} = query || {};
  const reloadSeconds =
    !processing_payment_reload_seconds ||
    typeof parseInt(processing_payment_reload_seconds) !== 'number' ||
    isNaN(parseInt(processing_payment_reload_seconds))
      ? false
      : parseInt(processing_payment_reload_seconds) * 1000;
  const values = membershipGenerator(account, companyType);
  const {isProcessingPayment} = values;

  const reloadDuration = validateMilliseconds(
    IS_STAGE_DEV
      ? reloadSeconds || DEFAULT_PROCESSING_PAYMENT_RELOAD_DURATION
      : DEFAULT_PROCESSING_PAYMENT_RELOAD_DURATION,
    MIN_PROCESSING_PAYMENT_RELOAD_DURATION,
    DEFAULT_PROCESSING_PAYMENT_RELOAD_DURATION
  );

  useReload(
    () => handleInitialSession(),
    isProcessingPayment ? reloadDuration : null
  );

  return (
    <MembershipContext.Provider value={values}>
      {children}
    </MembershipContext.Provider>
  );
}

const useMembership = () => useContext(MembershipContext);

export {MembershipContext, MembershipProvider, useMembership};
